import React, { useContext } from "react";
import { isEmpty } from "lodash";

import { UserContext } from "../amplify/authenticator-provider";
import useLang from "./useLang";
import { crmLinkGeneration } from "../components/engagements/activityCenter/opporotunityLayer/crmLinks";

const useCrm = () => {
  const [lang] = useLang();
  const { user } = useContext(UserContext);
  const { crmIntegrationType } = user.company;

  /**
   * Mapping of CRM types to their display names in the current language
   * @type {Object.<string, string>}
   */
  const CRM_TYPE_NAMES = {
    DS: lang.dealerSocket,
    DT: lang.dealerteam,
    SF: lang.salesforce,
    VIN: lang.vinSolutions,
    EL: lang.ELead,
    MF: lang.Momentum,
    ADF: lang.ADF,
  };

  /**
   * Maps CRM types to functions that extract opportunity IDs from different CRM systems
   * Each function handles the specific structure of its CRM's opportunity data
   * @type {Object.<string, function>}
   */
  const OPPORTUNITY_MAPPING = {
    DS: opp => opp?.dsOpportunityEvents?.[0]?.dsEventId,
    DT: opp => opp?.dtLeadOpportunities?.[0]?.dtOpportunityId,
    SF: opp => opp?.sfLeadOpportunities?.[0]?.sfOpportunityId,
    VIN: opp => opp?.vsLeadOpportunities?.[0]?.vsLeadId,
    EL: opp => opp?.elLeadOpportunities?.[0]?.elOpportunityId,
    MF: opp => opp?.mfLeadOpportunities?.[0]?.mfLeadId,
  };

  /**
   * Maps CRM types to functions that extract lead IDs from different CRM systems
   * Each function handles the specific structure of its CRM's lead data
   * @type {Object.<string, function>}
   */
  const LEAD_ID_MAPPING = {
    DS: lead => lead?.dsExtractedLead?.dsLeadId,
    DT: lead => lead?.dtExtractedLead?.dtLeadId,
    SF: lead => lead?.sfExtractedLead?.sfLeadId,
    MF: lead => lead?.mfExtractedLead?.mfProspectId || lead?.mfExtractedLead?.mfCustomerId,
  };

  /**
   * Gets the opportunity ID based on the current CRM integration type
   * @param {Array} leadOpportunities - Array of lead opportunities
   * @param {string} leadOpportunityId - Specific opportunity ID to find
   * @returns {string|null} The opportunity ID or null if not found
   */
  const getOpportunityId = (leadOpportunities, leadOpportunityId) => {
    if (isEmpty(leadOpportunities)) return null;

    const leadOpportunity = leadOpportunityId
      ? leadOpportunities.find(
          opportunity => opportunity.id.toString() === leadOpportunityId.toString()
        )
      : leadOpportunities[0];

    return OPPORTUNITY_MAPPING[crmIntegrationType]?.(leadOpportunity) || leadOpportunities[0]?.id;
  };

  /**
   * Checks if a lead opportunity exists in the current CRM system
   * @param {Object} leadOpportunity - The lead opportunity object to check
   * @returns {boolean} True if the opportunity exists, false otherwise
   */
  const checkIsOpportunity = leadOpportunity => {
    const hasOpportunityId = OPPORTUNITY_MAPPING[crmIntegrationType]?.(leadOpportunity);
    return Boolean(hasOpportunityId || leadOpportunity?.id);
  };

  /**
   * Retrieves all CRM-related details for a lead opportunity
   * @param {Object} leadOpportunity - The lead opportunity object
   * @param {Object} selectedLead - The selected lead object containing CRM data
   * @returns {Object} Object containing:
   *   - crmLink: URL to the CRM system
   *   - isOpportunity: Whether it's an opportunity
   *   - leadId: ID of the lead in the CRM
   *   - opportunityId: ID of the opportunity
   *   - linkOpprId: ID used for linking
   *   - salesForceType: Type of Salesforce integration
   */
  const getCrmDetails = (leadOpportunity, selectedLead) => {
    const salesForceType =
      {
        SF: "usAutoSales",
        DT: "dealerTeam",
      }[crmIntegrationType] || "";

    const isOpportunity = checkIsOpportunity(leadOpportunity);
    const leadId = LEAD_ID_MAPPING[crmIntegrationType]?.(selectedLead?.crmLeadObj);
    const opprId =
      OPPORTUNITY_MAPPING[crmIntegrationType]?.(leadOpportunity) || leadOpportunity?.id;

    const linkOpprId =
      crmIntegrationType === "VIN"
        ? leadOpportunity?.vsLeadOpportunities?.[0]?.vsContactId
        : crmIntegrationType === "EL"
        ? selectedLead?.fullName || ""
        : opprId;

    const crmLink = crmLinkGeneration(
      crmIntegrationType,
      isOpportunity,
      linkOpprId,
      leadId,
      salesForceType,
      user?.company?.id
    );

    return {
      crmLink,
      isOpportunity,
      leadId,
      opportunityId: opprId,
      linkOpprId,
      salesForceType,
    };
  };

  const crmTypeName = CRM_TYPE_NAMES[crmIntegrationType] || lang.smaiCrm;

  return { crmTypeName, getOpportunityId, getCrmDetails };
};

export default useCrm;
